;; Copyright (C) 2017, Doctor Evidence. All rights reserved.

(ns dre.shared.interface.search.pills
  "A search pills query."
  {:authors ["Jozef Wagner"]}
  (:refer-clojure :exclude [range])
  (:require #?(:cljs [cljs-time.core :as t]
               :clj [dre.time :as t])
            #?(:cljs [cljs-time.format :as f])
            #?(:cljs [goog.string :as gstring])
            [clojure.string :as str]
            #?(:clj [clojure.tools.logging :refer [warn]])
            [clojure.walk :as walk]
            [dre.coll :refer [assoc-when nn-hash-map several? single? somev]]
            [dre.shared.interface.search.blunt :as blunt]
            [dre.shared.interface.semgroups :refer [semgroup-label]]
            [dre.util :refer [preserving-reduced* rcompare str->int unkeyword]]))


;;;; Implementation details

;; TODO: This dummy info fn is used in cljs because requiring [taoensso.timbre :refer-macros [warn]] throws error in storybook
#?(:cljs
   (def warn (constantly nil)))

(declare uris-pill range-pill study-size-pill
         category-pill? or-pill? absolute-value)

(defn normalize-ranges
  "Returns a collection of normalized range pills.
  The input ranges should be grouped by the range field."
  [range-groups]
  (let [kf (fn [[field ranges]]
             (let [start
                   (when-let [coll (seq (keep :pill/start ranges))]
                     (let [acoll (map #(absolute-value field %) coll)
                           pairs (map vector coll acoll)]
                       (ffirst (sort-by second rcompare pairs))))
                   end (when-let [coll (seq (keep :pill/end ranges))]
                         (let [acoll (map #(absolute-value field %) coll)
                               pairs (map vector coll acoll)]
                           (ffirst (sort-by second compare pairs))))]
               (if (or (nil? start) (nil? end)
                       (not (pos? (compare start end))))
                 [(range-pill field start end)]
                 ranges)))]
    (mapcat kf range-groups)))

(defn normalize-study-sizes
  "Returns a collection of normalized study sizes pills."
  [study-size-groups]
  (let [kf (fn [study-sizes]
             (let [starts (map str->int
                               (filter #(not (nil? %))
                                       (map #(:pill/start %) (second study-sizes))))
                   start (if (empty? starts)
                           0
                           (apply max starts))
                   ends (filter #(not (nil? %))
                                (map #(str->int (:pill/end %)) (second study-sizes)))
                   ends (remove #(= 0 %) ends)
                   end (if (= 0 (count ends))
                         nil
                         (apply min ends))]
               (if (or (nil? start) (nil? end)
                       (not (pos? (compare start end)))
                       (= 0 end))
                 [(study-size-pill (str start) (str end))]
                 study-sizes)))]
    (mapcat kf study-size-groups)))

(defn study-size-pill?
  [{:pill/keys [type]}]
  (= :study-size type))

(defn normalize-and
  "Returns normalized and pill. Normalizes ranges. May return nil."
  [and-pill]
  (let [{:keys [:pill/pills]} and-pill
        mf #(if (= :and (:pill/type %)) (:pill/pills %) [%])
        pills (mapcat mf pills)
        range-pill? #(= :range (:pill/type %))
        ranges (group-by :pill/field (filter range-pill? pills))
        pills (if (some #(several? (second %)) ranges)
                (concat (normalize-ranges ranges)
                        (remove range-pill? pills))
                pills)
        study-sizes (group-by :pill/type (filter study-size-pill? pills))
        pills (concat (normalize-study-sizes study-sizes)
                      (remove study-size-pill? pills))
        pills (somev (distinct pills))]
    (cond (empty? pills) nil
          (single? pills) (first pills)
          :else (assoc and-pill :pill/pills pills))))

(defn normalize-boost
  "Returns normalized boost pill."
  [boost-pill]
  (let [{:keys [:pill/pills]} boost-pill
        mf #(if (= :boost (:pill/type %)) (:pill/pills %) [%])
        pills (mapcat mf pills)
        pills (somev (distinct pills))]
    (cond (empty? pills) nil
          (single? pills) (first pills)
          :else (assoc boost-pill :pill/pills pills))))

(defn normalize-or
  "Returns normalized or pill. Normalizes uri pills. May return nil."
  [or-pill]
  (let [{:keys [:pill/pills]} or-pill
        mf #(if (= :or (:pill/type %)) (:pill/pills %) [%])
        pills (mapcat mf pills)
        uri-pill? #(or (= :uris (:pill/type %)) (= :ai-sum-uris (:pill/type %)))
        uris (seq (filter uri-pill? pills))
        pills (if (several? uris)
                (cons (uris-pill (distinct (mapcat :pill/uris uris)))
                      (remove uri-pill? pills))
                pills)
        pills (somev (distinct pills))]
    (cond (empty? pills) nil
          (single? pills) (first pills)
          :else (assoc or-pill :pill/pills pills))))

(defn normalize-not
  "Returns normalized not pill. May return nil."
  [not-pill]
  (let [{:keys [:pill/pill]} not-pill]
    (cond (= :not (:pill/type pill)) (:pill/pill pill)
          (nil? pill) nil
          :else not-pill)))

(defn aux-category-pill?
  "Returns true if given pill may be used for representing auxiliary
  categories."
  [pill]
  (or (category-pill? pill)
      (and (or-pill? pill)
           (every? aux-category-pill? (:pill/pills pill)))))

(def shell-compound-types
  "A list of shell pill types.
  Shell pills are ones that contain other pills.
  Subject is not a shell type because a completed subject pill
  is considered empty based on its :pill/uris contents."
  #{:boost :and :or :with :not :author-disambiguated :sentence-tag :same-page})

(defn completely-empty-completed-pill?
  "Returns true if given completed pill is completely empty.
  Does not support blueprints."
  [completed-pill]
  (let [{:keys [:pill/type :pill/pills :pill/query :pill/uris
                :pill/pill :pill/start :pill/end :pill/values
                :pill/mode]} completed-pill]
    (case type
      (:and :or :with :boost) (empty? pills)
      (:affiliation-constraint
       :coauthor-constraint
       :email-constraint
       :id-constraint) (empty? values)
      (:sentence-tag :not) (nil? pill)
      (:subject :uris :ai-sum-uris) (and (empty? uris) (not mode))
      :range (and (nil? start) (nil? end))
      :study-size (and (nil? start) (nil? end))
      :syntax true
      :signal (empty? (:pill/label completed-pill))
      (str/blank? query)))) ;; :tag :text :category :source :author

(defn completely-empty-blueprint-pill?
  "Returns true if given blueprint pill is completely empty."
  [blueprint-pill]
  (let [{:keys [:pill/type :pill/pills :pill/query :pill/uris
                :pill/pill :pill/guid :pill/values
                :pill/start :pill/end]} blueprint-pill]
    (case type
      (:and :or :with :boost) (empty? pills)
      (:affiliation-constraint
       :coauthor-constraint
       :email-constraint
       :id-constraint) (empty? values)
      (:sentence-tag :not) (nil? pill)
      :subject (and (str/blank? guid) (empty? pills))
      :uris (empty? uris)
      :ai-sum-uris (empty? uris)
      :term (and (str/blank? guid) (str/blank? query))
      :range (and (nil? start) (nil? end))
      :study-size (and (nil? start) (nil? end))
      :syntax true
      :signal (empty? (:pill/query blueprint-pill))
      (str/blank? query)))) ;; :tag :text :category :source :author

(declare and-op or-op with-op not-op boost-op lbracket rbracket shell-pill?
         mode-op disambiguate-op author-pill sentence-tag-suffix-op same-page-op)

(defn contents-flat
  [pill]
  (let [opill pill
        {:keys [:pill/type :pill/pills :pill/pill
                :pill/query :pill/mode :pill/mode-label :pill/mode-args]} pill
        wrap #(cons lbracket (concat (interpose % %2) [rbracket]))]
    ;; TODO: complete blueprints is on flat pills, and flat term pills
    ;; do not have mode :(
    ;; Moreover, if we keep mode there, what if user deletes flat mode syntax pill
    (if mode
      (cons (mode-op mode mode-label mode-args)
            (contents-flat
             (-> opill
                 (dissoc :pill/mode :pill/uris :pill/mode-label :pill/mode-args))))
      (case (keyword type)
        :and (wrap and-op pills)
        :or (wrap or-op pills)
        :boost (wrap boost-op pills)
        :with (wrap with-op pills)
        :not [#_lbracket not-op pill #_rbracket]
        :author-disambiguated [lbracket (author-pill query) disambiguate-op pill rbracket]
        :sentence-tag [pill (sentence-tag-suffix-op query)]
        :same-page (wrap same-page-op pills)
        [opill]))))

(defn xpill->flat
  "Returns a transducer that transforms a given pill into flat pills."
  []
  (fn [rf]
    (let [prf (preserving-reduced* rf)]
      (fn
        ([] (rf))
        ([result] (rf result))
        ([result input]
         (if-not (shell-pill? input)
           (rf result input)
           (let [contents (contents-flat input)]
             (transduce (xpill->flat) (completing prf)
                        result contents))))))))

(defn- apply-modifiers
  [m input]
  (let [modifier (:cur/modifier m)
        near-modifier (:cur/near-modifier m)]
    (cond
      near-modifier (assoc input
                           :pill/mode modifier
                           :pill/near near-modifier)
      modifier (-> (assoc input :pill/mode modifier)
                   (assoc-when :pill/mode-label
                               (:cur/modifier-label m))
                   (assoc-when :pill/mode-args
                               (:cur/modifier-args m))
                   (dissoc :pill/uris))
      :else input)))

(defn xflats->pill
  "Returns a transducer that transforms a coll of flats into a pill."
  []
  (fn [rf]
    (let [parents (volatile! ())
          cur (volatile! nil)
          finalize-pill
          (fn finalize-pill [m input]
            (let [type (or (:cur/type m) :and)
                  type (if (= :bs type) :boost type)
                  input (apply-modifiers m input)
                  sentence-suffix? (= :sentence-tag-suffix
                                      (:pill/type input))
                  input (if (and (:cur/not m) (not sentence-suffix?))
                          {:pill/type :not
                           :pill/pill input}
                          input)
                  pill (cond
                         (= :disambiguate type)
                         (let [pill (:cur/pill m)
                               pills (:pill/pills pill)
                               lpill (or (last pills) pill)]
                           (when-not (= 1 (count pills))
                             (warn "author disambiguation format" pill))
                           (when-not (= :author (:pill/type lpill))
                             (throw
                              (ex-info
                               (str "Unsupported pill disambiguation type "
                                    (:pill/type lpill))
                               {})))
                           {:pill/type :author-disambiguated
                            :pill/query (:pill/query lpill)
                            :pill/pill input})
                         sentence-suffix?
                         (let [sts-pill {:pill/type :sentence-tag
                                         :pill/query (:pill/query input)
                                         :pill/pill (:cur/pill m)}
                               pill (:cur/pill m)]
                           (if-let [pills (seq (:pill/pills pill))]
                             ;; inject sts into last pill
                             (update-in pill
                                        [:pill/pills (dec (count pills))]
                                        #(assoc sts-pill :pill/pill %))
                             sts-pill))
                         :else
                         {:pill/type type
                          :pill/pills (somev [(:cur/pill m) input])})
                  {:keys [:pill/pills :pill/query]} pill
                  new-m (cond (or (not (str/blank? query))
                                  (seq pills))
                              {:cur/pill pill}
                              :else nil)]
              new-m))]
      (fn
        ([] (rf))
        ([result]
         (loop [pill (:cur/pill @cur)
                parents @parents]
           (if (seq parents)
             (recur (:cur/pill (finalize-pill (first parents) pill))
                    (next parents))
             (if pill
               (rf (unreduced (rf result pill)))
               (rf result)))))
        ([result input]
         (let [{:keys [:pill/type :pill/query]} input
               acur @cur]
           (if (= :syntax type)
             ;; syntax pill
             (let [query (when query (str/lower-case query))]
               (cond
                 ;; aux or malformed pill, skip
                 (or (= "aux" query) (not query))
                 result
                 ;; lbracket
                 (= "l" query)
                 (do
                   (vswap! parents conj acur)
                   (vreset! cur nil))
                 ;; rbracket
                 (= "r" query)
                 (let [parent (first @parents)
                       ncur (finalize-pill parent (:cur/pill acur))]
                   (vswap! parents next)
                   (vreset! cur ncur))
                 ;; not pill
                 (= "not" query)
                 (vswap! cur update :cur/not not)
                 ;; prefix pill modifier NEAR (specific to RegIntel)
                 (str/starts-with? query "ppmnear-")
                 (vswap! cur assoc-when
                         :cur/modifier (keyword (subs query 3))
                         :cur/near-modifier (subs query 8))
                 ;; prefix pill modifiers
                 (str/starts-with? query "ppm")
                 (vswap! cur assoc-when
                         :cur/modifier (keyword (subs query 3))
                         :cur/modifier-label (:pill/label input)
                         :cur/modifier-args (:pill/args input))
                 ;; sentence tag suffix
                 (str/starts-with? query "sts")
                 (let [input {:pill/type :sentence-tag-suffix
                              :pill/query (subs query 3)}]
                   (vreset! cur (finalize-pill acur input)))
                 ;; other syntax pill
                 :else
                 (vswap! cur assoc :cur/type (keyword query))))
             ;; ordinary pill
             (vreset! cur (finalize-pill acur input)))
           result))))))

(defn date->constraint
  [date]
  (when date
    #?(:cljs (gstring/format
              "%04d-%02d-%02d"
              (t/year date) (t/month date) (t/day date))
       :clj (t/print-iso-local-date date))))

(def formatters
  #?(:cljs
     [(f/formatter "yyyy")
      (f/formatter "yyyy-MM")
      (f/formatter "yyyy-MM-dd")]
     :clj
     [(t/formatter "yyyy")
      (t/formatter "yyyy-MM")
      (t/formatter "yyyy-MM-dd")]))

(defn mparse
  [f x]
  (when (< 3 (count x))
    (try
      #?(:cljs (f/parse f x)
         :clj (t/parse-local-date f x))
      #?(:cljs (catch :default _)
         :clj (catch Exception _e nil)))))


;;; Public API

(defn relative-date?
  "Checks whether a date is in relative format, e.g. d2"
  [field s]
  (and (or (= (name field) "published")
           (= (name field) "changed"))
       (string? s)
       (pos? (count s))
       #?(:clj (Character/isLetter ^Character (first s))
          :cljs (gstring/isAlpha (first s)))))

(defn absolute-value
  [field s]
  (if (relative-date? field s)
    (let [unit (first s)
          amount (when (< 1 (count s))
                   (str->int (subs s 1)))
          n #?(:cljs (t/now)
               :clj (t/today))
          t #?(:cljs (case unit
                       \m (t/minus n (t/months amount))
                       \d (t/minus n (t/days amount))
                       \y (t/minus n (t/years amount))
                       \w (t/minus n (t/weeks amount))
                       \n n)
               :clj (case unit
                      \m (t/minus-months n amount)
                      \d (t/minus-days n amount)
                      \y (t/minus-years n amount)
                      \w (t/minus-weeks n amount)
                      \n n))]
      (date->constraint t))
    s))

(defn term-pill
  "Returns a term pill that contains given uris."
  ([query uris]
   (nn-hash-map :pill/type :term
                :pill/uris (seq uris)
                :pill/query query))
  ([query source uris]
   (nn-hash-map :pill/type :term
                :pill/uris (seq uris)
                :pill/query query
                :pill/source source)))

(defn term-pill*
  [query label]
  (nn-hash-map :pill/type :term
               :pill/label label
               :pill/query query))

(defn sense-pill
  [sense label mode]
  (nn-hash-map :pill/type :term
               :pill/query sense
               :pill/label label
               :pill/mode mode))

(defn senses-pill
  ([senses label]
   (senses-pill senses label nil))
  ([senses label excluded]
   (nn-hash-map :pill/type :senses
                :pill/label label
                :pill/query (first senses)
                :pill/other-senses (next senses)
                :pill/excluded-senses excluded)))

(defn text-pill
  "Returns a new text pill."
  [text]
  {:pill/type :text
   :pill/query text})

(defn raw-text-pill
  "Returns a new text pill."
  [text]
  {:pill/type :text
   :pill/query text
   :pill/raw-text true})

(defn uris-pill
  "Returns a new uris pill."
  [uris]
  {:pill/type :uris
   :pill/uris (seq uris)})

(defn ai-sum-uris-pill
  "Returns a new uris pill."
  [uris]
  {:pill/type :ai-sum-uris
   :pill/uris (seq uris)})

(defn category-pill?
  "Returns true if given pill is a category pill."
  [pill]
  (= :category (:pill/type pill)))

(defn category-pill
  "Returns a new category pill."
  [category]
  {:pill/type :category
   :pill/query category})

(defn signal-pill?
  "Returns true if given pill is a signal pill."
  [pill]
  (= :signal (:pill/type pill)))

(defn signal-pill
  "Returns a new category pill"
  [{:keys [:signal/uuid :signal/title] :as _m}]
  {:pill/type :signal
   ;; pill is considered completed when it has a label pill/pills should NEVER
   ;; be stored in a pill, they are only resolved at search time.
   :pill/label title
   ;; the only stable identifier of a signal is the uuid
   :pill/query uuid})

(defn source-pill
  "Returns a new source pill."
  [source]
  {:pill/type :source
   :pill/query source})

(defn tag-pill
  "Returns a new tag pill."
  ([tag]
   {:pill/type :tag
    :pill/query tag})
  ([tag label]
   {:pill/type :tag
    :pill/query tag
    :pill/label label}))

(defn stag-pill
  "Returns a new sentence tag pill."
  [tag]
  {:pill/type :stag
   :pill/query tag})

(defn utag-pill
  "Returns a new user tag pill."
  ([tag]
   (utag-pill tag nil))
  ([tag _owner]
   {:pill/type :utag
    :pill/query tag}))

(defn split-author [s]
  (let [splits (str/split s #",")
        splits (if (= 1 (count splits))
                 (str/split s #" ")
                 splits)
        [lastname-parts firstname-parts] (if (= 1 (count splits))
                                           [splits nil]
                                           [(map str/trim (butlast splits)) (str/trim (last splits))])
        lastname (str/join " " lastname-parts)
        firstname firstname-parts]
    (when (< (count lastname-parts) 4)
      [lastname firstname])))

(defn author-pill
  "Returns a new author pill."
  ([lookup]
   {:pill/type :author
    :pill/query (->> (str/split lookup #"\b")
                     (map str/capitalize)
                     str/join)})
  ([lookup label]
   (if (str/blank? label)
     (author-pill lookup)
     (assoc (author-pill lookup) :pill/label label))))

(defn disambiguated-author-pill
  [lookup pill]
  {:pill/type :author-disambiguated
   :pill/query lookup
   :pill/pill pill})

(defn sentence-tag-pill
  [tag pill]
  {:pill/type :sentence-tag
   :pill/query tag
   :pill/pill pill})

(defn and-pill?
  "Returns true if a given pill is an and pill."
  [pill]
  (= :and (:pill/type pill)))

(defn and-pill
  "Returns a new and pill."
  [pills]
  {:pill/type :and
   :pill/pills (seq pills)})

(defn or-pill?
  "Returns true if a given pill is an or pill."
  [pill]
  (= :or (:pill/type pill)))

(defn or-pill
  "Returns a new or pill"
  [pills]
  {:pill/type :or
   :pill/pills (seq pills)})

(defn not-pill
  "Returns a new not pill"
  [pill]
  {:pill/type :not
   :pill/pill pill})

(defn boosted-pill
  "Returns a new boosted pill"
  [pill boost]
  {:pill/type :boosted
   :pill/pill pill
   :pill/query boost})

(defn shell-pill?
  "Returns true if pill is of :and, :or, :with, :not or :same-page type."
  [pill]
  (let [{:keys [:pill/type :pill/mode]} pill]
    (or mode
        (contains? shell-compound-types type))))

(defn ordinary-shell-pill?
  "Return true if pill is a shell one and has no other data attached
  putside :pill/pills."
  [pill]
  (and (shell-pill? pill)
       (nil? (:pill/mode pill))
       (str/blank? (:pill/query pill))))

(defn syntax-pill?
  "Returns true if pill is of a :syntax type"
  [pill]
  (= :syntax (:pill/type pill)))

(defn syntax-pill
  [query]
  (let [query (when-not (str/blank? query)
                (str/lower-case query))]
    {:pill/type :syntax
     :pill/query query}))

(defn range-pill?
  "Returns true if pill is a range one with a given field type."
  [pill field]
  (and (= :range (:pill/type pill))
       (= field (:pill/field pill))))

(defn dict-pill?
  [pill]
  (let [{:keys [:pill/type]} pill]
    (or (= :term type)
        (= :tag type)
        (= :author type))))

(declare main-pills aux-pills)

(defn dict-pills
  [pills]
  (->> (main-pills pills)
       (filter dict-pill?)
       set))

(defn range-pill
  "Returns a new range pill."
  [field start end]
  {:pill/type :range
   :pill/field field
   :pill/start start
   :pill/end end})

(defn study-size-pill
  ""
  [start end]
  {:pill/type :study-size
   :pill/start start
   :pill/end end})

(def lbracket
  {:pill/type :syntax
   :pill/query "l"})

(def rbracket
  {:pill/type :syntax
   :pill/query "r"})

(def and-op
  {:pill/type :syntax
   :pill/query "and"})

(defn mode-op
  ([mode]
   {:pill/type :syntax
    :pill/query (str "ppm" (unkeyword mode))})
  ([mode label args]
   (nn-hash-map
    :pill/type :syntax
    :pill/query (str "ppm" (unkeyword mode))
    :pill/label label
    :pill/args args)))

(defn sentence-tag-suffix-op
  "Returns a new sentence tag suffix op."
  ([query]
   {:pill/type :syntax
    :pill/query (str "sts" (unkeyword query))}))

(def or-op
  {:pill/type :syntax
   :pill/query "or"})

(def not-op
  {:pill/type :syntax
   :pill/query "not"})

(def with-op
  {:pill/type :syntax
   :pill/query "with"})

(def same-page-op
  {:pill/type :syntax
   :pill/query "same-page"})

(def aux-op
  {:pill/type :syntax
   :pill/query "aux"})

(def disambiguate-op
  {:pill/type :syntax
   :pill/query "disambiguate"})

(def boost-op
  {:pill/type :syntax
   :pill/query "bs"})

(defn aux-op?
  [pill]
  (and (= :syntax (:pill/type pill))
       (= "aux" (str/lower-case (or (:pill/query pill) "")))))

(defn tui->uri
  [tui]
  (str "http://purl.bioontology.org/ontology/STY/" tui))

(defn suggested-pill
  "Returns a pill based on a given suggestion."
  ([suggestion _label-map]
   (suggested-pill suggestion))
  ([suggestion]
   (let [{:keys [:suggestion/label :suggestion/key
                 :suggestion/canonical-label :suggestion/definitions
                 :suggestion/sense-features :suggestion/semgroups :suggestion/tag
                 :suggestion/label-group :suggestion/category
                 :suggestion/country-code
                 :suggestion/related]} suggestion
         semgroup-labels (keep #?(:clj
                                  semgroup-label
                                  :cljs
                                  (semgroup-label))
                               semgroups)
         pill (if tag
                (tag-pill (unkeyword tag) label)
                (term-pill* key label))
         category (when category (keyword category))]
     (-> pill
         (assoc-when :pill/related (seq (map suggested-pill related)))
         (assoc-when :pill/semgroup-labels semgroup-labels)
         (assoc-when :pill/sense-features sense-features)
         (assoc-when :pill/canonical-label canonical-label)
         (assoc-when :pill/definitions definitions)
         (assoc-when :pill/label-group label-group)
         (assoc-when :pill/country-code country-code)
         (assoc-when :pill/category category)))))

(defn coerce
  "Returns coerced pills. Useful for processing pills sent over
  the wire in JSON format.
  Input may be a pill or a collection of pills."
  [pills]
  (when pills
    (let [ks #{:pill/type :pill/mode :pill/field :pill/category}
          f (fn [[k v]]
              (let [k (if (string? k) (keyword k) k)
                    v (if (contains? ks k) (keyword v) v)]
                [k v]))
          pf (fn [x] (if (map? x) (into {} (map f x)) x))]
      (walk/postwalk pf pills))))

(defn xflatten
  "Returns a transducer that flattens pills, useful for subsequent
  extraction of some data, e.g. uris."
  []
  (fn [rf]
    (let [prf (completing (preserving-reduced* rf))]
      (fn
        ([] (rf))
        ([result] (rf result))
        ([result input]
         (let [ret (rf result input)]
           (if (reduced? ret)
             ret
             (->> (cons (:pill/pill input) (:pill/pills input))
                  (remove nil?)
                  (transduce (xflatten) prf ret)))))))))

(defn empty-completed-pill?
  "Returns true if given completed pill is empty or contains only
  empty pills. Does not support blueprints."
  [completed-pill]
  (let [rf (fn [_ pill]
             (when (and (not (completely-empty-completed-pill? pill))
                        (not (ordinary-shell-pill? pill)))
               (reduced true)))]
    (not (transduce (xflatten) (completing rf)
                    false [completed-pill]))))

(defn empty-blueprint-pill?
  "Returns true if given blueprint pill is empty or contains only
  empty pills."
  [blueprint-pill]
  (let [rf (fn [_ pill]
             (when (and (not (completely-empty-blueprint-pill? pill))
                        (not (ordinary-shell-pill? pill)))
               (reduced true)))]
    (not (transduce (xflatten) (completing rf)
                    false [blueprint-pill]))))

(defn incomplete-flat-blueprint-pill?
  ([blueprint-pill]
   (incomplete-flat-blueprint-pill? false blueprint-pill))
  ([_pill-details? blueprint-pill]
   (let [{:keys [:pill/type :pill/label]} blueprint-pill]
     (or (and (= :term type)
              true
              #_(or (str/blank? label) pill-details?))
         (= :senses type)
         (and (= :tag type)
              (str/blank? label))
         (and (= :sparql type)
              (not (seq (:pill/senses blueprint-pill))))))))

(defn incomplete-pill?
  [pill]
  (let [rf (fn [_ pill]
             (when (incomplete-flat-blueprint-pill? pill)
               (reduced true)))]
    (transduce (xflatten) (completing rf) false [pill])))

(defn normalize-text
  [pill]
  #?(:cljs pill
     :clj (let [{:keys [:pill/query]} pill]
            (when (and (not (str/blank? query))
                       (re-find (re-pattern "[a-zA-Z0-9?]") query))
              pill))))

(defn normalize
  "Returns a normalized pill, removing empty and unnecessary pills,
  grouping compound pills of same type, etc.
  Works only with completed pills, not with blueprints."
  [completed-pill]
  (let [mf (fn [{:keys [:pill/type] :as pill}]
             (condp = type
               :boost (normalize-boost pill)
               :and (normalize-and pill)
               :or (normalize-or pill)
               :not (normalize-not pill)
               :text (normalize-text pill)
               :synonym pill
               (when-not (empty-completed-pill? pill) pill)))
        pf (fn [x] (if (map? x) (mf x) x))]
    (walk/postwalk pf completed-pill)))

(defn normalize-blueprint
  "Returns a normalized pill, removing empty and unnecessary pills,
  grouping compound pills of same type, etc.
  Works only with blueprint pills."
  [blueprint-pill]
  (let [mf (fn [{:keys [:pill/type] :as pill}]
             (condp = type
               :boost (normalize-boost pill)
               :and (normalize-and pill)
               :or (normalize-or pill)
               :not (normalize-not pill)
               (when-not (empty-blueprint-pill? pill)
                 ;; c(medline.PubMed) becomes c(medline)
                 (dissoc pill :pill/label))))
        pf (fn [x] (if (map? x) (mf x) x))]
    (walk/postwalk pf blueprint-pill)))

(defn normalize-blueprint-new
  "Returns a normalized pill, removing empty and unnecessary pills,
  grouping compound pills of same type, etc.
  Works only with blueprint pills."
  [blueprint-pill]
  (let [mf (fn [{:keys [:pill/type] :as pill}]
             (condp = type
               :boost (normalize-boost pill)
               :and (normalize-and pill)
               :or (normalize-or pill)
               :not (normalize-not pill)
               pill))
        pf (fn [x] (if (map? x) (mf x) x))]
    (walk/postwalk pf blueprint-pill)))

(defn provide-pills
  "Returns a collection of pills from a given pill or pills.
  Extracts pills from and pill if given."
  [pill-or-pills]
  (if (map? pill-or-pills)
    (if (and-pill? pill-or-pills)
      (:pill/pills pill-or-pills)
      [pill-or-pills])
    pill-or-pills))

(defn pill-range
  "Returns a [start end] range from a given pill(s)."
  [field pill]
  (let [pill (->> (provide-pills pill)
                  (filter #(range-pill? % field))
                  first)]
    [(:pill/start pill) (:pill/end pill)]))

(defn today-constraint
  []
  (date->constraint #?(:cljs (t/now)
                       :clj (t/today))))

(defn tomorrow-constraint
  []
  (date->constraint
   #?(:cljs (t/plus (t/now) (t/days 1))
      :clj (t/plus-days (t/today) 1))))

(defn no-future-pill
  ([]
   (no-future-pill nil))
  ([_field]
   (range-pill
    :changed
    nil
    (tomorrow-constraint))))

(defn now-date
  []
  #?(:cljs (t/now)
     :clj (t/today)))

(defn type->date-ranges
  "Returns a [from to] date ranges for a pill,
  with a type interval, ending when the pill ends."
  ([pill type]
   (type->date-ranges pill type nil))
  ([pill type field]
   (let [to (if field
              (second (pill-range field pill))
              (or (second (pill-range :changed pill))
                  (second (pill-range :published pill))))
         to? to
         to (if to
              (or (first (keep #(mparse % to) formatters))
                  (now-date))
              (now-date))
         from #?(:cljs (cond
                         (= type :m1) (t/minus to (t/months 1))
                         (= type :m3) (t/minus to (t/months 3))
                         (= type :y1) (t/minus to (t/years 1))
                         (= type :y2) (t/minus to (t/years 2))
                         (= type :y5) (t/minus to (t/years 5))
                         (= type :y10) (t/minus to (t/years 10))
                         (= type :y20) (t/minus to (t/years 20))
                         (= type :all) nil
                         :else (t/minus to (t/months 6)))
                 :clj (cond
                        (= type :m1) (t/minus-months to 1)
                        (= type :m3) (t/minus-months to 3)
                        (= type :y1) (t/minus-years to 1)
                        (= type :y2) (t/minus-years to 2)
                        (= type :y5) (t/minus-years to 5)
                        (= type :y10) (t/minus-years to 10)
                        (= type :y20) (t/minus-years to 20)
                        (= type :all) nil
                        :else (t/minus-months to 6)))]
     [(when from (date->constraint from))
      (when to? (date->constraint to))])))


(defn pill-latest-end
  "Returns the latest absolute end field from all ranges present in the query."
  [field pill]
  (let [xform  (comp (xflatten)
                     (filter #(range-pill? % field))
                     (map :pill/end))
        rf (fn [x v]
             (let [av (absolute-value field v)]
               (if (and x (not (neg? (compare x av)))) x av)))]
    (transduce xform (completing rf) nil (provide-pills pill))))

(defn assoc-range
  "Returns pills with range set to the given values."
  ([pills field start]
   (assoc-range pills field start nil))
  ([pills field start end]
   (concat [(range-pill field start end)] (provide-pills pills))))

(defn assoc-study-size
  "Returns pills with study size set to the given values."
  ([pills start]
   (assoc-study-size pills start nil))
  ([pills start end]
   (concat [(study-size-pill start end)] (provide-pills pills))))

(defn pill-uris
  "Extract all uris from pill."
  [pill]
  (into [] (comp (xflatten) (mapcat :pill/uris)) [pill]))

(defn pills-senses
  "Extract all uris from pill."
  ([pills]
   (pills-senses pills nil))
  ([pills direct-only?]
   (let [mf #(cons (:pill/query %)
                   (concat (:pill/other-senses %)
                           (when-not direct-only? (:pill/senses %))))]
     (vec
      (distinct
       (into []
             (comp (xflatten)
                   (filter #(or (= :term (:pill/type %))
                                (= :senses (:pill/type %))))
                   (mapcat mf))
             pills))))))

(defn pills->flats
  [pills]
  (when-let [pills (seq (provide-pills pills))]
    (into [] (xpill->flat) pills)))

(defn flats->pill
  [flats]
  (let [pills (provide-pills flats)
        main-pills (main-pills pills)
        aux-pills (aux-pills pills)
        flatf #(first (into [] (xflats->pill) %))]
    (if (seq aux-pills)
      (and-pill [(flatf main-pills) (flatf aux-pills)])
      (flatf main-pills))))

(defn flats?
  [pills]
  (some syntax-pill? pills))

;;; Working with auxiliary pills

(defn main-pills
  [pills]
  (take-while (complement aux-op?) pills))

(defn aux-pills
  [pills]
  (rest (drop-while (complement aux-op?) pills)))

(defn aux
  [pills]
  (let [pills (provide-pills (flats->pill (aux-pills pills)))
        pill (some #(when (aux-category-pill? %) %) pills)
        xform (comp (xflatten)
                    (filter category-pill?)
                    (map :pill/query))]
    {:aux/categories (into [] xform [pill])}))

(defn aux-categories
  [pills]
  (:aux/categories (aux pills)))

(defn assoc-aux
  [pills aux-map]
  (let [{:keys [:aux/categories]} aux-map
        pills (main-pills pills)
        categories (seq (keep category-pill categories))]
    (if categories
      (concat pills [aux-op (or-pill categories)])
      pills)))

(defn update-main
  ([pills args]
   (apply update-main pills args))
  ([pills f & args]
   (let [main-pills (main-pills pills)
         aux-pills (aux-pills pills)]
     (if (seq aux-pills)
       (concat (apply f main-pills args)
               [aux-op]
               aux-pills)
       (apply f main-pills args)))))

(defn assoc-main-pills
  ([pills new-main-pills]
   (if-let [aux-pills (seq (aux-pills pills))]
     (concat new-main-pills [aux-op] aux-pills)
     new-main-pills)))

;;; Handling legacy query

(defn assoc-legacy-categories
  [pills categories]
  (let [categories (seq (keep category-pill categories))]
    (cond (several? categories)
          (update-main pills concat [and-op (or-pill categories)])
          (single? categories)
          (update-main pills concat (cons and-op categories))
          :else pills)))

(defn force-upgrade
  "Returns a search query map converted from a legacy version.
  Keeps new query map intact if given as input.
  Keeps non-pill query maps intact."
  [query]
  (let [{:keys [:query/pills]} query]
    (if (seq pills)
      (let [mf (fn [{:keys [:pill/label :pill/type] :as pill}]
                 (cond
                   (and (not (str/blank? label)) (= :term type))
                   (assoc pill :pill/query "REPLACE")
                   (and (not (str/blank? label)) (= :senses type))
                   (dissoc (assoc pill :pill/query "REPLACE" :pill/type :term)
                           :pill/other-senses)
                   :else pill))
            pills (map mf (coerce pills))]
        (assoc query :query/pills pills))
      query)))

(defn upgrade
  "Returns a search query map converted from a legacy version.
  Keeps new query map intact if given as input.
  Keeps non-pill query maps intact."
  [query]
  (let [{:keys [:query/categories :query/pills]} query]
    (if (and (seq pills) (seq categories))
      (let [pills (assoc-legacy-categories (coerce pills) categories)]
        (-> query
            (assoc :query/pills pills)
            (dissoc :query/categories)))
      (let [pills (coerce pills)
            {:keys [:aux/categories] :as aux} (aux pills)
            pills (-> pills
                      (assoc-aux (dissoc aux :aux/categories))
                      (assoc-legacy-categories categories))]
        (assoc query :query/pills pills)))))


;;;; Scratch

(defn =blunt
  "Returns normalized pill if given blunt strings represent
  equal normalized pills."
  [s1 s2]
  (when (= (normalize-blueprint (flats->pill (blunt/parse s1)))
           (normalize-blueprint (flats->pill (blunt/parse s2))))
    (normalize-blueprint (flats->pill (blunt/parse s2)))))

(comment

  (coerce {"pill/type" "term"
           "pill/query" "foo"
           "pill/mode" "descendants"})

  (coerce nil)

  (=blunt "foo,x(OR),x(L),bar,x(AND),baz,x(R)" "o(foo,a(bar,baz))")
  (=blunt "foo,bar,baz" "a(foo),o(bar),n(n(baz))")
  (=blunt "o(foo,bar,baz)" "o(foo,o(bar),o(o(baz)))")
  (=blunt "foo,bar,baz" "foo,a(bar,baz)")
  (=blunt "o(foo,bar,baz)" "o(foo,o(bar,baz))")
  (=blunt "d(2017,2018)" "d(2017,2019),d(2011,2018)")
  (=blunt "d(2017,2018),foo,baz"
          "foo,a(d(2017,2019),d(2011,2018),baz),d(2016,2020)")
  (flats->pill [(text-pill "foo")
                lbracket
                lbracket
                (text-pill "bar")
                rbracket
                or-op
                (text-pill "baz")
                rbracket])

  (flats->pill [(text-pill "foo")
                or-op
                not-op
                (text-pill "bar")
                and-op
                (text-pill "baz")])

  (blunt/print
   (pills->flats
    [(normalize
      (flats->pill
       (blunt/parse
        "u(foo).x(disambiguate).x(l).k(SUBAB).k(SUFKZ).x(r)")))]))

  (blunt/print
   [(flats->pill
     (pills->flats
      (normalize-blueprint
       (blunt/parse
        "gs(title,stroke),gs(abstract,a(heart,attack))"))))])

  (blunt/print
   [(flats->pill
     (pills->flats
      (normalize-blueprint
       (blunt/parse
        "k(SUEiE..descendants)"))))])

  (normalize-blueprint
   (flats->pill
    (blunt/parse
     "x(l).stroke.x(ststitle).x(r).x(l).x(l).heart.x(and).attack.x(r).x(stsabstract).x(r)")))

  (normalize-blueprint
   (flats->pill
    (blunt/parse
     "foo.bar.x(ststitle)")))

  (normalize-blueprint
   (flats->pill
    (blunt/parse
     "foo.x(l).bar.x(ststitle).x(r)")))

  (normalize-blueprint
   (flats->pill
    (blunt/parse
     "asdf.x(l).foo.bar.x(r).x(ststitle)")))

  (blunt/print
   (pills->flats
    (normalize-blueprint
     (blunt/parse
      "gs(title,stroke),gs(abstract,a(heart,attack))"))))

  (normalize-blueprint (flats->pill (blunt/parse "x(ppmdescendants).k(S0Elk.Heart)")))

  (blunt/print (blunt/parse "c(medline).r(123)"))
  (blunt/print [{:pill/type :term
                 :pill/query "foo"}])
  (flats->pill (blunt/parse "c(medline).r(123)")))
